import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
// components/avoir/SingleAvoir.js

import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import {
  Modal,
  Card,
  message,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Table,
  Form,
  Input,
  InputNumber,
  Space,
  Select,
  Popconfirm, Drawer, Descriptions, Dropdown,
  Tooltip
} from 'antd';
import {
  useFetchAvoirById,
  useFetchItemsInAvoir,
  useValidateAvoir1,
  useValidateAvoir3,
  useSaveAvoir, useUnvalidateAvoir,
  useFetchAvoirItemsPriceHistory
} from '../../hooks/avoirHooks';
import AddItemToAvoirForm from './AddItemToAvoirForm';
import { EditOutlined, DeleteOutlined, SaveOutlined, UndoOutlined, DownOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { usePaymentModes, useDeliveryModes } from '../../hooks/listsHooks';
import { useFetchComById } from '../../hooks/comHooks';
import { useFetchFacturesByClient } from '../../hooks/factureHooks';
import { useFetchAllEntrepots, useDefaultEntrepot } from '../../hooks/entrepotHooks';
import NestedAvoirTable from './NestedAvoirTable';
import ComList from '../com/ComList';
import ClientList from '../client/ClientList';
import { PrinterOutlined } from '@ant-design/icons';
import { printAvoirEtat1, printAvoirEtat3, printAvoirPcControl, printPriceHistoryTable } from './AvoirPrintUtility';
import { getClientById, getAllClients } from '../../models/clientAPI';
import AvoirRefEditor from './AvoirRefEditor';


const { Title, Text } = Typography;
const { Option } = Select;

const EditableContext = React.createContext(null);

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString() : "Not provided";

const formatTime = (date) => {
  if (!date) return '';
  try {
    // If it's an ISO string, extract time directly without timezone conversion
    if (typeof date === 'string' && date.includes('T')) {
      const [datePart, timePart] = date.split('T');
      const [time] = timePart.split('.');
      const [hours, minutes] = time.split(':');
      return `${hours}:${minutes}`;
    }
    // Fallback for Date objects
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const hours = d.getUTCHours().toString().padStart(2, '0');
    const minutes = d.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (e) {
    return '';
  }
};


const EditableField = ({ value, onChange, options, inputType = "input", displayValue, userPermissions, avoirStatus }) => {
  const canEdit = currentCan('returns', 'update') && (userPermissions?.isManager || (!avoirStatus?.VALIDER));
  
  const handleChange = (e) => {
    if (!canEdit && inputType === "select") return;
    const newValue = inputType === "select" ? e : e.target.value;
    onChange(newValue);
  };

  if (inputType === "select") {
    if (!canEdit) {
      // Ensure we render a string, not an object
      const displayText = displayValue || (typeof value === 'string' ? value : (value?.value || value?.label || String(value) || 'N/A'));
      return <span>{displayText}</span>;
    }
    
    // Handle both string and object options
    const getSelectValue = () => {
      if (!value) return undefined;
      if (typeof value === 'string') return value;
      // If value is an object, extract the value property
      return value?.value || value;
    };
    
    return (
      <Select
        value={getSelectValue()}
        onChange={handleChange}
        style={{ width: '200px' }}
        disabled={!canEdit}
        dropdownMatchSelectWidth={false}
        getPopupContainer={(trigger) => trigger.parentElement || document.body}
        allowClear={false}
      >
        {options?.map(option => {
          // Support both string options and object options {value, label}
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          return (
            <Option key={optionValue} value={optionValue}>
              {optionLabel}
            </Option>
          );
        })}
      </Select>
    );
  }

  return (
    <Input
      value={value}
      onChange={handleChange}
      disabled={!canEdit}
    />
  );
};

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      if (dataIndex === 'PV_TTC') {
        const newPvTtc = Number(values.PV_TTC);
        const tva = Number(record.TVA) || 0;
        const newPvHt = newPvTtc / (1 + tva / 100);

        handleSave({
          ...record,
          ...values,
          REMISE: 0,
          PV_HT: Math.round(newPvHt * 100) / 100
        });
      } else {
        handleSave({ ...record, ...values });
      }
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <InputNumber
          ref={inputRef}
          onPressEnter={save}
          onBlur={save}
          min={0}
          step={dataIndex === 'REMISE' ? 1 : 0.01}
          precision={2}
          max={dataIndex === 'REMISE' ? 100 : undefined}
        />
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" onClick={toggleEdit}>
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const SingleAvoir = ({ avoirId, onChangeView, onClose }) => {
  const {
    avoir: initialAvoir,
    loading: loadingAvoir,
    error: errorAvoir,
    refetch: refetchAvoir
  } = useFetchAvoirById(avoirId);

  const {
    items: initialItems,
    loading: loadingItems,
    error: errorItems,
    refetch: refetchItems
  } = useFetchItemsInAvoir(avoirId);

  const { validate1, error: validateError1 } = useValidateAvoir1();
  const { validate3, error: validateError3 } = useValidateAvoir3();
  const { handleSaveAvoir } = useSaveAvoir();

  const [localAvoir, setLocalAvoir] = useState(null);
  const [localItems, setLocalItems] = useState(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [itemsChanged, setItemsChanged] = useState(false);
  const [isAddItemModalVisible, setIsAddItemModalVisible] = useState(false);
  const [totals, setTotals] = useState({ totalHT: 0, totalTTC: 0 });
  const [comDrawerOpen, setComDrawerOpen] = useState(false);
  const [selectedCom, setSelectedCom] = useState('');
  const [commercialId, setCommercialId] = useState(null);
  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);
  const [selectedFactureRef, setSelectedFactureRef] = useState(null);
  const [isPriceHistoryOpen, setIsPriceHistoryOpen] = useState(false);
  const [clientDrawerOpen, setClientDrawerOpen] = useState(false);
  const [refreshingClient, setRefreshingClient] = useState(false);
  const { Com: commercialData, loading: loadingCom } = useFetchComById(commercialId);
  const { unvalidate } = useUnvalidateAvoir();
  
  // Get the list of entrepots
  const { entrepots, loading: loadingEntrepots } = useFetchAllEntrepots();
  const { defaultCode } = useDefaultEntrepot();
  const { modes: paymentModes } = usePaymentModes();
  const { modes: deliveryModes } = useDeliveryModes();
  
  // Fetch factures for current client
  const { factures, loading: loadingFactures, error: facturesError } = useFetchFacturesByClient(localAvoir?.COMPTE);

  // Build list of selected item codes for history lookup
  const codeArts = useMemo(() => {
    if (!Array.isArray(localItems)) return [];
    return Array.from(new Set(localItems.map(it => it?.CODE_ART).filter(Boolean)));
  }, [localItems]);

  const currentItemByCode = useMemo(() => {
    const map = {};
    if (Array.isArray(localItems)) {
      localItems.forEach(it => { if (it?.CODE_ART) map[it.CODE_ART] = it; });
    }
    return map;
  }, [localItems]);

  const {
    data: priceHistory,
    loading: loadingHistory,
    error: errorHistory,
    refetch: refetchHistory
  } = useFetchAvoirItemsPriceHistory(localAvoir?.COMPTE, codeArts, 3);



useEffect(() => {
  if (localAvoir?.CODE_COM) {
    setCommercialId(localAvoir.CODE_COM);
  }
}, [localAvoir?.CODE_COM]);

useEffect(() => {
  if (localAvoir?.REF_FAC) {
    setSelectedFactureRef(localAvoir.REF_FAC);
  }
}, [localAvoir?.REF_FAC]);

useEffect(() => {
  if (commercialData) {
    setSelectedCom(`${commercialData.nom} (${commercialData.code_com})`);
  }
}, [commercialData]);

const handleComSelect = (com) => {
  handleFieldChange("CODE_COM", com);
  setCommercialId(com);
  setComDrawerOpen(false);
};

const handleClientSelect = (client) => {
  setLocalAvoir((prev) => ({
    ...prev,
    CODE_CLT: client.CODE_CLT,
    CLIENT: client.CLIENT,
    COMPTE: client.COMPTE,
    MODE_PAIE: client.MODE_PAIE ?? prev.MODE_PAIE,
    MODELIV: client.MODELIV ?? prev.MODELIV,
    CODE_COM: client.CODE_COM ?? prev.CODE_COM,
    REF_FAC: null, // Clear facture reference when client changes
    client: {
      ...(prev?.client || {}),
      ...client,
    },
  }));
  if (client?.CODE_COM) {
    setCommercialId(client.CODE_COM);
    setSelectedCom("");
  }
  // Clear selected facture reference when client changes
  setSelectedFactureRef(null);
  setHasLocalChanges(true);
  setClientDrawerOpen(false);
  message.success(`Client changé: ${client.CLIENT} (${client.COMPTE})`);
};

const refreshClientInfo = async () => {
  try {
    if (!localAvoir) return;
    setRefreshingClient(true);
    // Prefer id from joined client; fallback by COMPTE search
    let latestClient = null;
    if (localAvoir.client?.id) {
      const res = await getClientById(localAvoir.client.id);
      latestClient = res.data;
    } else if (localAvoir.COMPTE) {
      const result = await getAllClients({ compteSearch: localAvoir.COMPTE, pageSize: 1 });
      latestClient = result.clients?.[0] || null;
    }
    if (!latestClient) {
      message.warning("Client introuvable pour actualisation.");
      return;
    }
    setLocalAvoir((prev) => ({
      ...prev,
      CODE_CLT: latestClient.CODE_CLT ?? prev.CODE_CLT,
      CLIENT: latestClient.CLIENT ?? prev.CLIENT,
      COMPTE: latestClient.COMPTE ?? prev.COMPTE,
      MODELIV: latestClient.MODELIV ?? prev.MODELIV,
      MODE_PAIE: latestClient.MODE_PAIE ?? prev.MODE_PAIE,
      client: {
        ...(prev?.client || {}),
        ...latestClient,
      },
    }));
    setHasLocalChanges(true);
    message.success("Infos client actualisées.");
  } catch (e) {
    message.error(`Échec d'actualisation du client: ${e.message || e}`);
  } finally {
    setRefreshingClient(false);
  }
};
  // Sync initialAvoir to localAvoir when initialAvoir changes
  useEffect(() => {
    if (initialAvoir) {
      setLocalAvoir({ ...initialAvoir });
    }
  }, [initialAvoir]);

  // Prefill default entrepot when avoir has none (editable only)
  useEffect(() => {
    if (!localAvoir || !defaultCode) return;
    if (localAvoir.VALIDER) return;
    if (localAvoir.CODE_ENT) return;

    setLocalAvoir(prev => (prev && !prev.CODE_ENT ? { ...prev, CODE_ENT: defaultCode } : prev));
    setHasLocalChanges(true);
  }, [
    defaultCode,
    localAvoir?.id,
    localAvoir?.CODE_ENT,
    localAvoir?.VALIDER,
  ]);

  // Sync initialItems to localItems when initialItems changes
  useEffect(() => {
    if (initialItems) {
      setLocalItems([...initialItems]);
    }
  }, [initialItems]);

   // Calculate totals whenever items change
   useEffect(() => {
    if (localItems) {
      calculateTotals();
    }
  }, [localItems]);

  const calculateTotalTTC = (qte = 0, pvTTC = 0, remise = 0) => {
    const discountedPrice = pvTTC * (1 - (remise || 0) / 100);
    const total = qte * discountedPrice;
    return Math.round(total * 100) / 100;
  };

  const calculateTotalHT = (qte = 0, pvHT = 0, remise = 0) => {
    const discountedPrice = pvHT * (1 - (remise || 0) / 100);
    const total = qte * discountedPrice;
    return Math.round(total * 100) / 100;
  };

  const calculateTotals = () => {
    let totalTTC = 0;
    let totalHT = 0;

    const updatedItems = localItems.map((item) => {
      // Calculate TTC independently from PV_TTC
      const itemTotalTTC = calculateTotalTTC(item.QTE, item.PV_TTC, item.REMISE);

      // Calculate HT independently from PV_HT (not derived from TTC)
      const itemTotalHT = calculateTotalHT(item.QTE, item.PV_HT, item.REMISE);

      totalTTC += itemTotalTTC;
      totalHT += itemTotalHT;

      return {
        ...item,
        TotalHT: itemTotalHT,
        TotalTTC: itemTotalTTC
      };
    });

    // Check if items actually changed before updating
    const hasItemsChanged = !localItems.every((item, index) => {
      const updatedItem = updatedItems[index];
      return updatedItem &&
             item.TotalHT === updatedItem.TotalHT &&
             item.TotalTTC === updatedItem.TotalTTC;
    });

    if (hasItemsChanged) {
      setLocalItems(updatedItems);
    }

    // Round final totals to 2 decimal places
    totalTTC = Math.round(totalTTC * 100) / 100;
    totalHT = Math.round(totalHT * 100) / 100;

    setTotals({ totalTTC, totalHT });
    setLocalAvoir(prev => ({
      ...prev,
      MNT_TTC: -totalTTC,  // Negative for avoir
      MNT_HT: -totalHT     // Negative for avoir
    }));
  };

  const handleSave = (row) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item.CODE_ART === row.CODE_ART ? { ...item, ...row } : item
      )
    );
    setHasLocalChanges(true);
    setItemsChanged(true); // Mark that items were modified
    message.success("Item updated locally. Click 'Save Changes' to persist.");
  };

  const handleSaveChanges = async () => {
    try {
      // Ensure avoir only includes necessary fields, explicitly including CODE_ENT
      const avoirData = {
        REF_AVR: localAvoir.REF_AVR,
        CODE_CLT: localAvoir.CODE_CLT,
        CLIENT: localAvoir.CLIENT,
        COMPTE: localAvoir.COMPTE,
        DATE_AVR: localAvoir.DATE_AVR,
        MNT_HT: localAvoir.MNT_HT,
        MNT_TTC: localAvoir.MNT_TTC,
        CODE_COM: localAvoir.CODE_COM,
        REMARQUE: localAvoir.REMARQUE,
        BASEHT: localAvoir.BASEHT,
        VALIDER: localAvoir.VALIDER || false,
        REF_FAC: localAvoir.REF_FAC ?? null,
        CODE_ENT: localAvoir.CODE_ENT // Explicitly include CODE_ENT
      };
      
      // Only send items if they were actually modified
      // If only REF_AVR changed, don't send items - CASCADE will handle it
      await handleSaveAvoir({
        avoir: avoirData,
        items: itemsChanged ? localItems : undefined, // Only send items if they changed
        oldRefAvoir: initialAvoir?.REF_AVR // Pass old REF_AVR to detect changes
      });

      setHasLocalChanges(false);
      setItemsChanged(false); // Reset items changed flag
      message.success("Avoir saved successfully!");
      refetchAvoir();
      refetchItems();
    } catch (error) {
      message.error(`Failed to save avoir: ${error.message}`);
    }
  };

  const handleDelete = (codeArt) => {
    setLocalItems((prevItems) => prevItems.filter(item => item.CODE_ART !== codeArt));
    setHasLocalChanges(true);
    setItemsChanged(true); // Mark that items were modified
    message.success("Item deleted locally. Click 'Save Changes' to persist.");
  };


  const handleValidate1Click = async () => {
    try {
      await validate1(avoirId);
      message.success('Avoir1 has been successfully validated.');
      await Promise.all([
        refetchAvoir(),
        refetchItems()
      ]);
      setHasLocalChanges(false);
    } catch (error) {
      message.error(`Failed to validate avoir1: ${error.message}`);
    }
  };

  const handleValidate3Click = async () => {
    try {
      await validate3(avoirId);
      message.success('Avoir3 has been successfully validated.');
      await Promise.all([
        refetchAvoir(),
        refetchItems()
      ]);
      setHasLocalChanges(false);
    } catch (error) {
      message.error(`Failed to validate avoir3: ${error.message}`);
    }
  };

  const handleReplaceWithFacture = () => {
    if (!selectedFactureRef) {
      message.warning('Veuillez sélectionner une facture.');
      return;
    }

    const selectedFacture = factures.find(f => f.REF_FAC === selectedFactureRef);
    if (!selectedFacture) {
      message.error('Facture sélectionnée introuvable.');
      return;
    }

    if (!selectedFacture.detailFactures || selectedFacture.detailFactures.length === 0) {
      message.warning('Cette facture n\'a pas d\'articles à copier.');
      return;
    }

    Modal.confirm({
      title: 'Remplacer tous les articles',
      content: `Cette action va supprimer tous les articles existants dans l'avoir et les remplacer par les ${selectedFacture.detailFactures.length} articles de la facture ${selectedFactureRef}. Êtes-vous sûr de vouloir continuer?`,
      okText: 'Oui, remplacer',
      cancelText: 'Annuler',
      type: 'warning',
      onOk: () => {
        // Convert facture details to avoir format
        const newItems = selectedFacture.detailFactures.map(detail => {
          const remise = detail.REMISE || 0;
          const tva = detail.TVA || 0;
          const totalTTC = calculateTotalTTC(detail.QTE, detail.PV_TTC, remise);
          const totalHT = calculateTotalHT(detail.QTE, detail.PV_HT, remise);

          return {
            CODE_ART: detail.CODE_ART,
            ARTICLE: detail.ARTICLE,
            QTE: detail.QTE,
            GRATUIT: detail.GRATUIT || 0,
            PV_HT: detail.PV_HT,
            PV_TTC: detail.PV_TTC,
            PA_HT: detail.PA_HT,
            REMISE: remise, // Preserve remise from facture
            REMISEG: detail.REMISEG || 0,
            TVA: tva,
            TotalHT: totalHT,
            TotalTTC: totalTTC,
            MAJ_STK: true
          };
        });

        // Replace all items (clear existing and set new items)
        setLocalItems(newItems);

        // Update the avoir with the selected facture reference
        setLocalAvoir(prevAvoir => ({
          ...prevAvoir,
          REF_FAC: selectedFactureRef
        }));

        setHasLocalChanges(true);
        setItemsChanged(true); // Mark that items were modified so they get saved
        // Don't clear selectedFactureRef - keep it to show the selection
        message.success(`Tous les articles ont été remplacés par les ${newItems.length} articles de la facture ${selectedFactureRef}`);
      }
    });
  };

 // Modified handleUnvalidateClick in SingleAvoir component
const handleUnvalidateClick = async () => {
  Modal.confirm({
    title: 'Unvalidate Avoir',
    content: 'Are you sure you want to unvalidate this avoir?',
    okText: 'Yes, Unvalidate',
    cancelText: 'No, Cancel',
    type: 'warning',
    onOk: async () => {
      try {
        await unvalidate(localAvoir.REF_AVR);
        await Promise.all([
          refetchAvoir(),
          refetchItems()
        ]);
        setHasLocalChanges(false);
        message.success('Avoir has been successfully unvalidated.');
      } catch (error) {
        // Show error in a more prominent way using Modal for important errors
        if (error.message.includes('reglement')) {
          Modal.error({
            title: 'Cannot Unvalidate Avoir',
            content: (
              <div>
                <p>{error.message}</p>
                <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
                  Please remove this avoir from the reglement first before attempting to unvalidate it.
                </p>
              </div>
            ),
            okText: 'I Understand',
            width: 500,
          });
        } else {
          // Use regular message for other types of errors
          message.error({
            content: error.message,
            duration: 5,
            style: {
              marginTop: '20vh',
            },
          });
        }
      }
    },
  });
};

  const handleClose = () => {
    if (typeof onChangeView === 'function') {
      onChangeView('list');
    } else if (typeof onClose === 'function') {
      onClose();
    } else if (window && window.history) {
      window.history.back();
    }
  };

const handlePrint = async (printType) => {
  try {
      if (!localAvoir || !localItems) {
          throw new Error('Missing required data for printing');
      }

      switch(printType) {
        case 'etat1':
          await printAvoirEtat1(localAvoir, localItems, commercialData);
          break;
        case 'etat3':
          await printAvoirEtat3(localAvoir, localItems, commercialData);
          break;
        case 'pccontrol':
          await printAvoirPcControl(localAvoir, localItems, commercialData);
          break;
        default:
          message.error('Please select a print format');
          return;
      }
      
      message.success('Document prepared for printing');
  } catch (error) {
      console.error('Error printing avoir:', error);
      message.error(`Failed to print: ${error.message}`);
  }
};
  

  const handleAddItemSuccess = (payload) => {
    const itemsToAdd = Array.isArray(payload) ? payload : [payload];
    setLocalItems((prevItems) => {
      const updated = [...prevItems];
      itemsToAdd.forEach((newItem) => {
        if (!newItem || !newItem.CODE_ART) return;
        const existingIndex = updated.findIndex((it) => it.CODE_ART === newItem.CODE_ART);
        if (existingIndex !== -1) {
          updated[existingIndex] = { ...updated[existingIndex], ...newItem };
        } else {
          updated.push(newItem);
        }
      });
      return updated;
    });
    setHasLocalChanges(true);
    setItemsChanged(true); // Mark that items were modified
    setIsAddItemModalVisible(false);
    message.success(itemsToAdd.length > 1 ? `${itemsToAdd.length} articles ajoutés` : 'Article ajouté');
  };

  const handleFieldChange = (field, value) => {
    setLocalAvoir(prevAvoir => ({
      ...prevAvoir,
      [field]: value
    }));
    setHasLocalChanges(true);
  };

  const columns = [
    {
      title: "Code Art",
      dataIndex: "CODE_ART",
      key: "CODE_ART",
    },
    {
      title: "Article",
      dataIndex: "ARTICLE",
      key: "ARTICLE",
    },
    {
      title: "Réf OEM",
      dataIndex: "REF_OEM",
      key: "REF_OEM",
      render: (text, record) => record.REF_OEM || record.article?.REF_OEM || '',
    },
    {
      title: "Quantity",
      dataIndex: "QTE",
      key: "QTE",
      editable: true,
    },
    {
      title: "PV TTC",
      dataIndex: "PV_TTC",
      key: "PV_TTC",
      editable: true,
      render: (text) => ` ${parseFloat(text).toFixed(2)}`,
    },
    {
      title: "Discount (%)",
      dataIndex: "REMISE",
      key: "REMISE",
      editable: true,
      render: (text) => `${parseFloat(text).toFixed(2)}%`,
    },
    {
      title: "Total TTC",
      dataIndex: "TotalTTC",
      key: "TotalTTC",
      render: (_, record) => {
        const total = calculateTotalTTC(record.QTE, record.PV_TTC, record.REMISE);
        return ` ${total.toFixed(2)}`;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this item?"
          onConfirm={() => handleDelete(record.CODE_ART)}
          okText="Yes"
          cancelText="No"
        >
          <PermissionButton permissionModule="returns" permissionAction="update" icon={<DeleteOutlined />} danger>
            Delete
          </PermissionButton>
        </Popconfirm>
      ),
    },
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const editableColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });


  const getDescriptionItems = () => [
    {
      key: 'clientInfo',
      label: 'Client Info',
      children: (
        <div>
          <Row gutter={16}>
            <Col span={14}>
              <Text strong>Client Name:</Text> {localAvoir.CLIENT} {localAvoir.client?.VILLE ? `- ${localAvoir.client.VILLE}` : ''}
            </Col>
            <Col span={8}>
              <Text strong>Client Account:</Text> {localAvoir.COMPTE || "N/A"}
              <Space size="small" style={{ marginLeft: 8 }}>
                {(!localAvoir.VALIDER) && (
                  <PermissionButton permissionModule="returns" permissionAction="update"
                    onClick={() => setClientDrawerOpen(true)} 
                    type="link"
                    icon={<EditOutlined />}
                    size="small"
                    style={{ padding: '0 4px' }}
                    title="Changer le client"
                  />
                )}
                <Button 
                  onClick={refreshClientInfo}
                  type="link"
                  icon={<ReloadOutlined />}
                  size="small"
                  loading={refreshingClient}
                  style={{ padding: '0 4px' }}
                  title="Actualiser les données client"
                />
              </Space>
            </Col>
          </Row>
          <Row style={{ marginTop: '8px' }}>
            <Col span={24}>
              <Text strong>Conditions:</Text> {localAvoir.client?.conditions || "Aucune"}
              <Tooltip 
                title={localAvoir.client?.NOTES || "pas de notes"}
                placement="top"
              >
                <InfoCircleOutlined 
                  style={{ 
                    color: '#1890ff', 
                    fontSize: '14px', 
                    cursor: 'help',
                    marginLeft: '8px'
                  }} 
                />
              </Tooltip>
            </Col>
          </Row>
        </div>
      ),
      span: 2,
    },
    {
      key: 'refFacture',
      label: 'Reference Facture',
      children: localAvoir?.VALIDER || localAvoir?.REF_FAC ? (
        <Space>
          <span>{localAvoir.REF_FAC}</span>
          {!localAvoir?.VALIDER && localAvoir?.REF_FAC && (
            <Button 
              type="link" 
              size="small"
              onClick={() => {
                setLocalAvoir(prevAvoir => ({
                  ...prevAvoir,
                  REF_FAC: null
                }));
                setSelectedFactureRef(null);
                setHasLocalChanges(true);
              }}
            >
              Changer
            </Button>
          )}
        </Space>
      ) : (
        <Space>
          <Select
            placeholder="Sélectionner une facture"
            style={{ width: 200 }}
            value={selectedFactureRef}
            onChange={(value) => {
              if (value === '__CLEAR_ALL__') {
                Modal.confirm({
                  title: 'Vider tous les articles',
                  content: 'Cette action va supprimer tous les articles de l\'avoir. Êtes-vous sûr de vouloir continuer?',
                  okText: 'Oui, vider',
                  cancelText: 'Annuler',
                  type: 'warning',
                  onOk: () => {
                    setLocalItems([]);
                    setLocalAvoir(prevAvoir => ({
                      ...prevAvoir,
                      REF_FAC: null
                    }));
                    setSelectedFactureRef(null);
                    setHasLocalChanges(true);
                    message.success('Tous les articles ont été supprimés');
                  }
                });
              } else {
                setSelectedFactureRef(value);
              }
            }}
            showSearch
            optionFilterProp="children"
            loading={loadingFactures}
            disabled={loadingFactures}
            filterOption={(input, option) => {
              if (option?.value === '__CLEAR_ALL__') return true;
              return (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }}
          >
            <Select.Option key="__CLEAR_ALL__" value="__CLEAR_ALL__" style={{ color: 'red', fontWeight: 'bold' }}>
              🗑️ Vider tous les articles
            </Select.Option>
            <Select.OptGroup label="Factures disponibles">
              {factures && factures.map(facture => (
                <Select.Option key={facture.REF_FAC} value={facture.REF_FAC}>
                  {facture.REF_FAC} - {facture.DATE_FAC ? new Date(facture.DATE_FAC).toLocaleDateString() : ''} - {(facture.MNT_TTC || 0).toFixed(2)} MAD
                </Select.Option>
              ))}
            </Select.OptGroup>
          </Select>
          <Button 
            type="primary" 
            size="small"
            onClick={handleReplaceWithFacture}
            disabled={!selectedFactureRef}
          >
            Remplacer
          </Button>
        </Space>
      ),
      span: 1,
    },
    {
      key: 'creationDate',
      label: 'Creation Date',
      children: formatDate(localAvoir.DATE_AVR),
      span: 1,
    },
    {
      key: 'remarks',
      label: 'Remarks',
      children: (
        <EditableField
          value={localAvoir.REMARQUE}
          onChange={(value) => handleFieldChange("REMARQUE", value)}
        />
      ),
      span: 2,
    },
    {
      key: 'amountHT',
      label: 'Amount HT',
      children: ` ${Math.abs(localAvoir.MNT_HT).toFixed(2)}`,
      span: 1,
    },
    {
      key: 'amountTTC',
      label: 'Amount TTC',
      children: ` ${Math.abs(localAvoir.MNT_TTC).toFixed(2)}`,
      span: 1,
    },
    {
      key: 'commercialCode',
      label: 'Commercial',
      children: (
        <Space>
          {loadingCom ? (
            <Spin size="small" />
          ) : (
            <span>
              {selectedCom || (commercialData ? 
                `${commercialData.nom} (${commercialData.code_com})` : 
                `ID: ${localAvoir.CODE_COM}`)}
            </span>
          )}
          <PermissionButton permissionModule="returns" permissionAction="update"
            onClick={() => setComDrawerOpen(true)} 
            type="link"
            icon={<EditOutlined />}
            size="small"
            style={{ padding: '4px' }}
          />
        </Space>
      ),
      span: 1,
    },
    {
      key: 'validationDate',
      label: 'Validation Date',
      children: localAvoir.DATEVALID ? formatDate(localAvoir.DATEVALID) : 'Not validated',
      span: 1,
    },
    {
      key: 'entrepot',
      label: 'Entrepôt',
      children: (
        <EditableField
          value={localAvoir.CODE_ENT}
          onChange={(value) => {
            handleFieldChange("CODE_ENT", value);
          }}
          options={entrepots?.map(e => ({ value: e.code_ent, label: `${e.code_ent} - ${e.name}` })) || []}
          inputType="select"
          userPermissions={{ isManager: true }} // For now, allow managers to edit
          avoirStatus={{
            VALIDER: localAvoir.VALIDER
          }}
          displayValue={localAvoir.entrepot ? `${localAvoir.entrepot.code_ent} - ${localAvoir.entrepot.name}` : (localAvoir.CODE_ENT || 'Non défini')}
        />
      ),
      span: 1,
    },
  ];
  
  

  if (loadingAvoir || loadingItems) return <Spin tip="Loading..." />;
  if (errorAvoir || errorItems) return <Alert message="Error" description={errorAvoir || errorItems} type="error" showIcon />;
  if (!localAvoir || !localItems) return <Alert message="No avoir found" type="info" showIcon />;

  return (
    <div>
      <Card
        title={
          <Space align="center">
            <Text strong style={{ fontSize: '16px' }}>Avoir - </Text>
            <AvoirRefEditor
              refAvr={localAvoir.REF_AVR}
              onSuccess={(newRef) => {
                // Refetch to get updated data
                refetchAvoir();
                refetchItems();
              }}
              disabled={!localAvoir.VALIDER}
            />
            {localAvoir.VALIDER ? (
              <Tag color="green">
                Validé
              </Tag>
            ) : (
              <Tag color="orange">
                Non validé
              </Tag>
            )}
            {localAvoir.DATEVALID ? (
              <span style={{ color: "#888888", fontSize: "0.85em", marginLeft: "4px" }}>
                {formatDate(localAvoir.DATEVALID)} {formatTime(localAvoir.HEUREVALID)}
              </span>
            ) : ''}
          </Space>
        }
        bordered={false}
        extra={
          <Space>
            {hasLocalChanges && (
              <PermissionButton
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler les modifications',
                    content: "Êtes-vous sûr de vouloir annuler toutes les modifications? Cela rétablira l'avoir à l'état sauvegardé précédemment.",
                    okText: 'Oui, Annuler tout',
                    cancelText: 'Non, Garder',
                    onOk: () => {
                      setLocalAvoir({ ...initialAvoir });
                      setLocalItems([...initialItems]);
                      setHasLocalChanges(false);
                      setItemsChanged(false); // Reset items changed flag
                      message.success('Toutes les modifications ont été annulées');
                    }
                  });
                }}
              >
                Annuler modif.
              </PermissionButton>
            )}
            {!localAvoir.VALIDER && (
              <>
                <PermissionButton permissionModule="returns" permissionAction="validate" type="primary" onClick={handleValidate1Click} disabled={hasLocalChanges}>
                  Valider AV1
                </PermissionButton>
                <PermissionButton permissionModule="returns" permissionAction="validate" type="primary" onClick={handleValidate3Click} disabled={hasLocalChanges} style={{ marginLeft: 8 }}>
                  Valider AV3
                </PermissionButton>
              </>
            )}
            {localAvoir.VALIDER && (
              <PermissionButton permissionModule="returns" permissionAction="unvalidate"
                type="primary"
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler la validation',
                    content: "Êtes-vous sûr de vouloir annuler la validation de cet avoir ?",
                    okText: 'Oui, Annuler',
                    cancelText: 'Non',
                    type: 'warning',
                    onOk: handleUnvalidateClick
                  });
                }}
              >
                Annuler validation
              </PermissionButton>
            )}
            <PermissionButton permissionModule="returns" permissionAction="update" type="primary" onClick={handleSaveChanges} disabled={!hasLocalChanges}>
              Enregistrer Modif.
            </PermissionButton>
            <Dropdown 
              menu={{
                items: [
                  {
                    key: 'etat1',
                    label: 'État 1',
                    onClick: () => handlePrint('etat1')
                  },
                  {
                    key: 'etat3', 
                    label: 'État 3',
                    onClick: () => handlePrint('etat3')
                  },
                  {
                    key: 'pccontrol',
                    label: 'PC Contrôle',
                    onClick: () => handlePrint('pccontrol')
                  }
                ]
              }} 
              trigger={['click']}
            >
              <PermissionButton permissionModule="returns" permissionAction="print" icon={<PrinterOutlined />}>
                Imprimer <DownOutlined />
              </PermissionButton>
            </Dropdown>
            <Button 
              onClick={() => {
                setIsPriceHistoryOpen(true);
                refetchHistory?.();
              }}
            >
              Historique prix (3 ans)
            </Button>
            <Button 
              type="default"
              onClick={handleClose}
              style={{ 
                backgroundColor: 'white',
                color: '#EE2625',
                border: '1px solid #EE2625',
              }}
            >
              SORTIR
            </Button>
          </Space>
        }
      >
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          items={getDescriptionItems()}
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        />
      </Card>
  
      <Card 
        title="Articles dans l'avoir" 
        style={{ marginTop: 20 }} 
        extra={
          <Space>
            <PermissionButton permissionModule="returns" permissionAction="update"
              onClick={() => setIsAddItemDrawerVisible(true)}
              type="primary"
              disabled={localAvoir.VALIDER}
            >
              Ajouter Article
            </PermissionButton>
          </Space>
        }
      >
        <NestedAvoirTable
          items={localItems}
          handleSave={handleSave}
          handleDelete={handleDelete}
          isValidated={localAvoir.VALIDER}
          codeEnt={localAvoir?.CODE_ENT}
        />
      </Card>
  
      <Drawer
        title="Ajouter un article à l'avoir"
        width={980}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
        extra={
          <Space>
            <PermissionButton   onClick={() => setIsAddItemDrawerVisible(false)}>Fermer</PermissionButton>
          </Space>
        }
        styles={{ body: { paddingBottom: 16, paddingTop: 16 } }}
      >
        <AddItemToAvoirForm
          refAvoir={localAvoir.REF_AVR}
          codeEnt={localAvoir?.CODE_ENT}
          onSuccess={(newItems) => {
            handleAddItemSuccess(newItems);
            setIsAddItemDrawerVisible(false);
          }}
        />
      </Drawer>
  
      <Drawer
        title="Sélectionner un commercial"
        width={720}
        onClose={() => setComDrawerOpen(false)}
        open={comDrawerOpen}
      >
        <ComList onSelectCom={handleComSelect} />
      </Drawer>

      <Drawer
        title="Sélectionner un client"
        width={720}
        onClose={() => setClientDrawerOpen(false)}
        open={clientDrawerOpen}
      >
        <ClientList onSelectClient={handleClientSelect} />
      </Drawer>

      <Drawer
        title="Historique des prix - 3 dernières années"
        width={980}
        onClose={() => setIsPriceHistoryOpen(false)}
        open={isPriceHistoryOpen}
        extra={
          <Space>
            <PermissionButton permissionModule="returns" permissionAction="print"
              icon={<PrinterOutlined />}
              onClick={async () => {
                try {
                  await printPriceHistoryTable(localAvoir, localItems, priceHistory);
                  message.success('Impression lancée');
                } catch (err) {
                  message.error(`Erreur d'impression: ${err.message}`);
                }
              }}
              disabled={!priceHistory || priceHistory.length === 0}
            >
              Imprimer
            </PermissionButton>
            <Button onClick={() => refetchHistory?.()}>Rafraîchir</Button>
            <Button onClick={() => setIsPriceHistoryOpen(false)}>Fermer</Button>
          </Space>
        }
        styles={{ body: { paddingBottom: 16, paddingTop: 16 } }}
      >
        {errorHistory ? (
          <Alert type="error" message={errorHistory} showIcon />
        ) : null}
        <Spin spinning={!!loadingHistory} tip="Chargement de l'historique...">
          <div style={{ border: '1px solid #f0f0f0', borderRadius: '2px' }}>
            {/* Main table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '140px 2fr 140px 120px',
              backgroundColor: '#fafafa',
              padding: '12px 16px',
              borderBottom: '2px solid #f0f0f0',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              <div>Code Art</div>
              <div>Article</div>
              <div>PV catalogue TTC</div>
              <div>PV avoir TTC</div>

            </div>

            {(priceHistory || []).map((ph, phIdx) => {
              const current = currentItemByCode[ph.codeArt];
              const history = ph.history || [];

              return (
                <div key={ph.codeArt} style={{ borderBottom: phIdx < priceHistory.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  {/* Main row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '140px 2fr 140px 120px',
                    backgroundColor: '#fff',
                    padding: '8px 16px',
                    borderBottom: history.length > 0 ? '1px solid #f0f0f0' : 'none',
                    fontSize: '14px'
                  }}>
                    <div>{ph.codeArt}</div>
                    <div>{ph.article?.ARTICLE || current?.ARTICLE || ''}</div>
                    <div>{ph.article?.PV_TTC != null ? ` ${Number(ph.article.PV_TTC).toFixed(2)}` : ''}</div>
                    <div>{current?.PV_TTC != null ? ` ${Number(current.PV_TTC).toFixed(2)}` : ''}</div>
                    
                  </div>

                  {/* Nested rows */}
                  {history.length > 0 && (
                    <div style={{ paddingLeft: '32px', backgroundColor: '#fafafa' }}>
                      {/* Header for nested table */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '120px 120px 100px 100px 100px 80px',
                        backgroundColor: '#e6f7ff',
                        padding: '8px 16px',
                        borderBottom: '1px solid #d9d9d9',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        <div>Facture</div>
                        <div>Date</div>
                        <div>PV TTC</div>
                        <div>PV HT</div>
                        <div>Remise</div>
                        <div>Qté</div>
                      </div>

                      {/* History rows */}
                      {history.map((h, hIdx) => {
                        const dateFac = h.dateFac ? new Date(h.dateFac) : null;
                        const formattedDate = dateFac ? 
                          `${String(dateFac.getDate()).padStart(2, '0')}/${String(dateFac.getMonth() + 1).padStart(2, '0')}/${dateFac.getFullYear()}` 
                          : '';
                        
                        return (
                        <div
                          key={`${ph.codeArt}-${hIdx}`}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '120px 120px 100px 100px 100px 80px',
                            padding: '8px 16px',
                            borderBottom: hIdx < history.length - 1 ? '1px solid #d9d9d9' : 'none',
                            fontSize: '13px',
                            backgroundColor: '#fff',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span 
                              style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: h.isCold ? '#1890ff' : '#ff7a45',
                                display: 'inline-block',
                                flexShrink: 0
                              }}
                              title={h.isCold ? 'Cold' : 'Not Cold'}
                            />
                            {h.refFac}
                          </div>
                          <div>{formattedDate}</div>
                          <div>{` ${Number(h.pvTTC).toFixed(2)}`}</div>
                          <div>{` ${Number(h.pvHT).toFixed(2)}`}</div>
                          <div>{`${Number(h.remise).toFixed(2)}%`}</div>
                          <div>{Number(h.qte)}</div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Spin>
      </Drawer>

    </div>
  );
}
export default SingleAvoir;
